import { Alert, Button } from 'antd';
import React from 'react';
import { useSelector } from 'react-redux';

import FullPageLoader from '../../../foundation/components/full_page_loader/FullPageLoader';
import Modal from '../../../foundation/components/modal/Modal';
import ResizableTable from '../../../foundation/components/resizable_table/ResizableTable';
import { LINK_BLUE } from '../../../foundation/config/colors';
import { useGetVariantLinkoutsQuery } from '../../api/variantApi';
import { useVariantAccess } from '../hooks/useVariantAccess';
import { selectVariantLock } from '../redux';
import ArgumentModal from './ArgumentModal';
import ClassificationTitle from './ClassificationTitle';
import { StructuredArgument } from './helpers/structureArgumentsData';
import { useArgumentsClassificationModalLogic } from './hooks';

type ArgumentsClassificationModalType = {
  closeModal: () => void;
  dataSource: StructuredArgument[];
  onRow: (record: StructuredArgument) => {
    onClick: () => void;
    'data-is-stage'?: string;
    'data-first-in-section'?: string;
    'data-last-in-section'?: string;
    'data-section-group'?: string;
  };
  variantId: string;
};

const ArgumentsClassificationModal = ({
  closeModal,
  onRow,
  dataSource,
  variantId,
}: ArgumentsClassificationModalType) => {
  const lock = useSelector(selectVariantLock);
  const { canInteract, isReadOnly } = useVariantAccess();
  // Per the RTK Query tutorial: use query hooks to access cached data directly.
  const { data: linkouts } = useGetVariantLinkoutsQuery(
    { variant_id: variantId },
    { skip: !variantId },
  );

  // All logic extracted to custom hook for separation of concerns
  const {
    isSaving,
    isArgumentModalVisible,
    selectedArgument,
    internalSource,
    setInternalSource,
    modalOpts,
    columns,
    hasChanges,
    handleSaveChanges,
    closeArgumentModal,
  } = useArgumentsClassificationModalLogic({
    closeModal,
    dataSource,
    variantId,
  });

  return (
    <>
      {isArgumentModalVisible && selectedArgument && (
        <ArgumentModal
          closeModal={closeArgumentModal}
          argument={selectedArgument}
          source={internalSource}
          updateSource={setInternalSource}
        />
      )}
      <Modal
        {...modalOpts}
        className="h-classification-modal"
        footer={
          <div className="c-form__btn-wrapper argument-modal__form-btn-wrapper">
            <Button
              type="primary"
              className="argument-modal__form-submit-btn"
              disabled={!hasChanges || !canInteract}
              onClick={handleSaveChanges}
            >
              Save changes
            </Button>
          </div>
        }
      >
        {isSaving && <FullPageLoader />}

        {isReadOnly && lock && (
          <Alert
            message="Read-Only Mode"
            description={`This variant is currently locked by ${lock.locked_by} and cannot be edited.`}
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        <ClassificationTitle />
        {linkouts?.cspec_url && (
          <div style={{ marginBottom: 8 }}>
            <a
              href={linkouts.cspec_url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: LINK_BLUE,
                textDecoration: 'none',
                fontWeight: 400,
                fontSize: 16,
              }}
            >
              CSpec
            </a>{' '}
            by ClinGen are available for the gene
          </div>
        )}
        <div className="modal__table-wrapper">
          <ResizableTable
            columns={columns}
            dataSource={internalSource}
            pagination={false}
            rowKey={(record, index) =>
              `${record.stage}-${record.section}-${index}`
            }
            className="interpretation__classification-table"
            onRow={onRow}
            fitToContainer
            persistenceKey="arguments-classification-modal-fit-v5"
          />
        </div>
      </Modal>
    </>
  );
};

export default ArgumentsClassificationModal;
